"use client";

import { useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import { T, useTextLocale, tl } from "@/components/t";
import { SectionHeader, Tint, Tile, StatGrid, Legend } from "@/components/finance/Finance";
import { Card, Icon, Stack } from "@/design";
import { track } from "@/lib/analytics";
import { storage } from "@/lib/storage";
import { tarif, zvEof } from "@/lib/finance-engine";
import styles from "./PayslipVerlauf.module.css";

/** localStorage key — written by the upload() success path in FinanzenPayslipV2
 *  (see PATCH.py), read here. Shape: PayslipHistoryEntry[], capped at 24,
 *  deduped by month. Only month/brutto/netto/ts — never raw payslip data. */
export const PAYSLIP_HISTORY_KEY = "ck_payslip_history";

export interface PayslipHistoryEntry {
  month: string;
  brutto: number;
  netto: number;
  ts: number;
}

const MONTHS_DE = [
  "januar", "februar", "märz", "april", "mai", "juni",
  "juli", "august", "september", "oktober", "november", "dezember",
];

/** "März 2026" / "03/2026" → sortable index; null when unparseable. */
function monthKey(e: PayslipHistoryEntry): number | null {
  const m = (e.month || "").toLowerCase().trim();
  const byName = m.match(/([a-zäöüß]+)[^0-9]*([0-9]{4})/);
  if (byName) {
    const idx = MONTHS_DE.findIndex((n) => byName[1].startsWith(n.slice(0, 3)));
    if (idx >= 0) return parseInt(byName[2], 10) * 12 + idx;
  }
  const numeric = m.match(/([0-9]{1,2})[./-]([0-9]{4})/);
  if (numeric) {
    const mm = parseInt(numeric[1], 10);
    if (mm >= 1 && mm <= 12) return parseInt(numeric[2], 10) * 12 + (mm - 1);
  }
  return null;
}

export default function PayslipVerlauf({
  deductions,
  example,
}: {
  /** deductions of the CURRENT analysis — drives the honest Erstattungs-Hinweis */
  deductions?: { label: string; amount: number }[];
  /** true when the surrounding result is the /example payload — suppresses the hint */
  example?: boolean;
}) {
  const loc = useTextLocale();
  // Storage is read in a mount effect only (SSR/hydration safety): first render
  // is null on server and client, the section appears post-hydration.
  const [history, setHistory] = useState<PayslipHistoryEntry[] | null>(null);

  useEffect(() => {
    const raw = storage.getJSON<PayslipHistoryEntry[]>(PAYSLIP_HISTORY_KEY, []);
    const clean = (Array.isArray(raw) ? raw : []).filter(
      (e) => !!e && typeof e.brutto === "number" && e.brutto > 0
        && typeof e.netto === "number" && typeof e.ts === "number",
    );
    setHistory(clean);
    if (clean.length >= 2) {
      const avgBrutto = clean.reduce((s, e) => s + e.brutto, 0) / clean.length;
      const lst = example ? 0 : ((deductions || []).find((d) => /lohnsteuer/i.test(d.label || ""))?.amount ?? 0);
      track("payslip_verlauf_view", {
        months: clean.length,
        erstattung_hint: lst > 0 && tarif(zvEof(Math.round(avgBrutto * 12))) === 0,
      });
    }
    // props are stable for the lifetime of one result view — run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!history || history.length < 2) return null;

  const sorted = [...history].sort((a, b) => {
    const ka = monthKey(a);
    const kb = monthKey(b);
    if (ka != null && kb != null && ka !== kb) return ka - kb;
    return a.ts - b.ts;
  });
  const shown = sorted.slice(-12);
  const n = sorted.length;
  const avgBrutto = sorted.reduce((s, e) => s + e.brutto, 0) / n;
  const avgNetto = sorted.reduce((s, e) => s + e.netto, 0) / n;
  const annualBrutto = Math.round(avgBrutto * 12);
  const annualNetto = Math.round(avgNetto * 12);
  const maxBrutto = Math.max(...shown.map((e) => e.brutto), 1);
  const lohnsteuerMo = example ? 0 : ((deductions || []).find((d) => /lohnsteuer/i.test(d.label || ""))?.amount ?? 0);
  // Honest check via the engine, never re-derived: annualised zvE approximation
  // (AN-Pauschbetrag + Sonderausgaben + ~80% SV via zvEof), then §32a tarif().
  // tarif() === 0 ⇔ projected taxable income is under the Grundfreibetrag.
  const erstattungLikely = lohnsteuerMo > 0 && tarif(zvEof(annualBrutto)) === 0;

  const fmt = (v: number) => `${Math.round(v).toLocaleString("de-DE")}€`;
  const labelFor = (e: PayslipHistoryEntry) =>
    e.month || new Date(e.ts).toLocaleDateString(loc === "en" ? "en-GB" : "de-DE", { month: "short", year: "numeric" });

  const clearHistory = () => {
    storage.remove(PAYSLIP_HISTORY_KEY);
    setHistory([]);
  };

  return (
    <div>
      <SectionHeader
        title={<T de="Dein Gehaltsverlauf" en="Your salary history" />}
        hint={tl(loc, `${n} Abrechnungen · nur lokal in deinem Browser`, `${n} payslips · stored only in your browser`)}
      />
      <Card variant="muted">
        <Stack gap="4">
          <div className={styles.rows}>
            {shown.map((e, i) => (
              <div key={`${e.ts}-${i}`} className={styles.row}>
                <span className={styles.month}>{labelFor(e)}</span>
                <div className={styles.bars}>
                  <div className={styles.barLine}>
                    <span className={styles.track}>
                      <span
                        className={styles.fillBrutto}
                        style={{ width: `${Math.max(4, (e.brutto / maxBrutto) * 100)}%` }}
                      />
                    </span>
                    <span className={styles.amt}>{fmt(e.brutto)}</span>
                  </div>
                  <div className={styles.barLine}>
                    <span className={styles.track}>
                      <span
                        className={styles.fillNetto}
                        style={{ width: `${Math.max(4, (e.netto / maxBrutto) * 100)}%` }}
                      />
                    </span>
                    <span className={styles.amt}>{fmt(e.netto)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <Legend
            items={[
              { color: "var(--ck-primary)", label: tl(loc, "Brutto", "Gross") },
              { color: "var(--ck-success)", label: tl(loc, "Netto", "Net") },
            ]}
          />
          {n > shown.length && (
            <p className={styles.moreNote}>
              <T de={`Zeigt die letzten ${shown.length} von ${n} Abrechnungen.`} en={`Showing the last ${shown.length} of ${n} payslips.`} />
            </p>
          )}

          <StatGrid cols={2}>
            <Tile
              label={<T de="Jahres-Hochrechnung Brutto" en="Projected gross / year" />}
              value={`~${annualBrutto.toLocaleString("de-DE")}€`}
              sub={tl(loc, `Ø aus ${n} Abrechnungen × 12`, `avg of ${n} payslips × 12`)}
            />
            <Tile
              tone="pos"
              label={<T de="Jahres-Hochrechnung Netto" en="Projected net / year" />}
              value={`~${annualNetto.toLocaleString("de-DE")}€`}
              sub={tl(loc, "Schätzung, kein Steuerbescheid", "estimate, not a tax assessment")}
            />
          </StatGrid>

          {erstattungLikely && (
            <Tint tone="green">
              <p className={styles.hintTitle}>
                <T de="Deine Lohnsteuer gibt es wohl komplett zurück" en="Your withheld income tax is likely fully refundable" />
              </p>
              <p className={styles.hintText}>
                <T
                  de={`Auf dieser Abrechnung werden ${lohnsteuerMo.toLocaleString("de-DE", { minimumFractionDigits: 2 })}€ Lohnsteuer einbehalten — hochgerechnet liegt dein zu versteuerndes Jahreseinkommen aber unter dem Grundfreibetrag. Die Jahres-Einkommensteuer wäre damit 0€, und einbehaltene Lohnsteuer holst du dir über die Steuererklärung zurück.`}
                  en={`This payslip withholds ${lohnsteuerMo.toLocaleString("de-DE", { minimumFractionDigits: 2 })}€ of income tax — but your projected annual taxable income is below the basic allowance. Annual income tax would be 0€, so withheld tax typically comes back in full via a tax return.`}
                />
              </p>
              <p className={styles.hintCaveat}>
                <T
                  de="Faustregel-Schätzung: Ø-Brutto × 12 mit Standard-Abzügen (§32a-Tarif), ohne Steuerklasse, Freibeträge und weitere Einkünfte. Verbindlich ist nur dein Steuerbescheid — im Zweifel unabhängige Beratung (z. B. Lohnsteuerhilfeverein)."
                  en="Rule-of-thumb estimate: avg gross × 12 with standard deductions (§32a schedule), ignoring tax class, allowances and other income. Only your tax assessment is binding — when in doubt, get independent advice."
                />
              </p>
              <a
                className={styles.cta}
                href="https://www.elster.de/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => track("payslip_erstattung_cta")}
              >
                <T de="Steuererklärung starten — ELSTER (offiziell & kostenlos) →" en="Start your tax return — ELSTER (official & free) →" />
              </a>
            </Tint>
          )}

          <div className={styles.footerRow} data-noprint="">
            <p className={styles.privacyNote}>
              <T de="Gespeichert werden nur Monat, Brutto und Netto — lokal, nie auf unseren Servern." en="Only month, gross and net are stored — locally, never on our servers." />
            </p>
            <button type="button" className={styles.clearBtn} onClick={clearHistory}>
              <Icon icon={Trash2} decorative />
              <T de="Verlauf löschen" en="Clear history" />
            </button>
          </div>
        </Stack>
      </Card>
    </div>
  );
}
